import { useRef } from "react";
import {
  FirebasePublicUrl,
  ProductResultsV1Url,
  ProductResultsV2Url,
} from "../constants";

const useProductResults = () => {
  const productsRef = useRef(null);

  const resetProductResults = () => {
    productsRef.current = null;
  };

  const fetchProductResultsV1 = async (apiToken, lng) => {
    if (productsRef.current) return productsRef.current;

    try {
      const response = await fetch(`${ProductResultsV1Url}?lng=${lng}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        method: ["GET"],
      });
      if (response.ok) {
        const data = await response.json();

        if (!data || typeof data !== "object") {
          throw new Error("Invalid API response");
        }

        let rawProducts;
        if (Array.isArray(data)) {
          rawProducts = data;
        } else if (Array.isArray(data.recommendations)) {
          rawProducts = data.recommendations;
        } else {
          const values = Object.values(data);
          rawProducts =
            values.length > 0 && values.every((v) => Array.isArray(v))
              ? Object.entries(data).flatMap(([category, items]) =>
                  (items ?? []).map((product) => ({
                    ...product,
                    __category: category,
                  })),
                )
              : values.filter(
                  (v) => v && typeof v === "object" && !Array.isArray(v),
                );
        }

        const recommendations = rawProducts.map((product) => {
          // Backend may send price as "67,00 € - GLOW28" — split into price + promo.
          const [rawPrice = "", rawPromo] = (product.price ?? "")
            .split(" - ")
            .map((s) => s.trim());
          const category =
            product.category ??
            product.__category ??
            (Array.isArray(product.categories)
              ? product.categories[0]
              : undefined);
          return {
            ...product,
            name: product.name ?? product.title,
            price: rawPrice,
            promoCode: rawPromo || undefined,
            imageUrl:
              product.imageUrl ||
              (product.imageStoragePath
                ? `${FirebasePublicUrl}${encodeURIComponent(
                    product.imageStoragePath.replace(/^customerConfigs\//, ""),
                  )}?alt=media`
                : undefined),
            categories: category?.trim() ? [category] : ["All-Categories"],
            brand: product.brand?.trim() ? product.brand : "All-Brands",
          };
        });

        const categories = [
          ...new Set(
            recommendations
              .flatMap((p) => p.categories)
              .filter((c) => c && c !== "All-Categories"),
          ),
        ].sort();

        const brands = [
          ...new Set(
            recommendations
              .map((p) => p.brand)
              .filter((b) => b && b !== "All-Brands"),
          ),
        ].sort();

        productsRef.current = {
          categories,
          brands,
          recommendations,
        };
        return productsRef.current;
      } else {
        throw new Error("Failed to fetch!");
      }
    } catch (error) {
      productsRef.current = {
        categories: [],
        brands: [],
        recommendations: [],
      };
      return { categories: [], brands: [], recommendations: [] };
    }
  };

  const fetchProductResultsV2 = async (apiToken, lng) => {
    if (productsRef.current) return productsRef.current;

    try {
      const response = await fetch(`${ProductResultsV2Url}?lng=${lng}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        method: ["GET"],
      });
      if (response.ok) {
        const data = await response.json();

        if (
          !data ||
          (Array.isArray(data) && data.length === 0) ||
          (typeof data === "object" && Object.keys(data).length === 0)
        ) {
          throw new Error("Empty or invalid data");
        }

        const recommendations = (data.recommendations ?? []).map((product) => {
          const [rawPrice = "", rawPromo] = (product.price ?? "")
            .split(" - ")
            .map((s) => s.trim());
          return {
            ...product,
            price: rawPrice,
            promoCode: rawPromo || undefined,
          };
        });

        productsRef.current = {
          ...data,
          recommendations,
        };
        return productsRef.current;
      } else {
        throw new Error("Failed to fetch!");
      }
    } catch (error) {
      productsRef.current = {
        categories: [],
        brands: [],
        recommendations: [],
      };
      return { categories: [], brands: [], recommendations: [] };
    }
  };

  return {
    productsRef,
    resetProductResults,
    fetchProductResultsV1,
    fetchProductResultsV2,
  };
};

export default useProductResults;
